import { memo, useCallback, useMemo, useState, useContext } from "react";
import { Box, useTheme } from "@mui/material";
import PropTypes from "prop-types";

import { ButtonV1, ModalBox } from "components";
import FGPickUpDetailsModal from "./FGPickUpDetailsModal";

import { postCall_v2 } from "services";
import { useToaster } from "hooks";

import { error, success, JOMS_API_BASE_V1_URL } from "constants";
import FGRefreshContext from "../FGRefreshContext";

const FGDoPendingActions = ({ order }) => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const refreshDataInBackground = useContext(FGRefreshContext);

  const [isPickModelOpen, setIsPickModelOpen] = useState(false);

  const {
    error: { dark },
  } = theme.palette;

  const isApproveDisabled = useMemo(
    () => +order?.paymentRequest > 0,
    [order?.creditRequest, order?.paymentRequest],
  );

  const isApproveButtonDisabled = useMemo(
    () =>
      order?.deliveryType === "JOTS" &&
      (!order?.pickupSlotFrom || !order?.pickupSlotTo),
    [order?.deliveryType, order?.pickupSlotFrom, order?.pickupSlotTo],
  );

  const doApproval = useCallback(
    async (action) => {
      try {
        const actionMapper = {
          APPROVE: "APPROVED",
          REJECT: "REJECTED",
        };
        const payload = {
          preDoReleaseRtabId: order?.preDoId,
          preDoReleaseRequestStatus: actionMapper[action],
        };
        await postCall_v2(
          `${JOMS_API_BASE_V1_URL}/v1/inventory-data/fg-change-status`,
          payload,
        );
        triggerToaster(
          `Status changed to ${actionMapper[
            action
          ]?.toLowerCase()} successfully.`,
          success,
        );
        refreshDataInBackground();
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Please try again later",
          error,
        );
      }
    },
    [order?.preDoId, refreshDataInBackground, triggerToaster],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/refresh-fg/${order?.preDoId}`,
      );
      triggerToaster("Successfully refreshed details", success);
      refreshDataInBackground();
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  }, [order?.preDoId, refreshDataInBackground, triggerToaster]);

  const handleSavePickUpDetails = useCallback(
    async (payload) => {
      try {
        const res = await postCall_v2(
          `${JOMS_API_BASE_V1_URL}/v1/inventory-data/seller-and-pickup-details`,
          payload,
        );
        if (res?.data) {
          triggerToaster("Pick Up details updated successfully", success);
          setIsPickModelOpen(false);
          refreshDataInBackground();
        }
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Please try again later",
          error,
        );
      }
    },
    [refreshDataInBackground, triggerToaster],
  );

  const handleOpenPickUpModal = useCallback(() => {
    setIsPickModelOpen(true);
  }, []);

  const handleClosePickUpModal = useCallback(() => {
    setIsPickModelOpen(false);
  }, []);

  return (
    <Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "primary.light",
          borderRadius: 1.5,
          margin: 2,
          padding: 5,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          justifyContent: "flex-end",
        }}
        gap={2}
      >
        <ButtonV1
          style={{
            color: dark,
            border: `1px solid ${dark}`,
            "&:hover": { border: `1px solid ${dark}` },
          }}
          variant="outlined"
          title="Reject"
          color="error"
          size="small"
          onClick={() => doApproval("REJECT")}
        />
        <ButtonV1
          title="Approve"
          size="small"
          disabled={isApproveDisabled || isApproveButtonDisabled}
          onClick={() => doApproval("APPROVE")}
        />
        <ButtonV1
          variant="contained"
          title="Refresh"
          size="small"
          onClick={handleRefresh}
        />
        <ButtonV1
          variant="outlined"
          title="Add Pickup Location"
          size="small"
          onClick={handleOpenPickUpModal}
        />
      </Box>

      <ModalBox
        onCloseModal={handleClosePickUpModal}
        open={isPickModelOpen}
        width="70%"
      >
        <FGPickUpDetailsModal
          order={order}
          onCloseModal={handleClosePickUpModal}
          onSavePickUpDetails={handleSavePickUpDetails}
        />
      </ModalBox>
    </Box>
  );
};

FGDoPendingActions.propTypes = {
  order: PropTypes.shape({
    preDoId: PropTypes.number,
    paymentRequest: PropTypes.number,
    creditRequest: PropTypes.number,
    deliveryType: PropTypes.string,
    pickupSlotFrom: PropTypes.string,
    pickupSlotTo: PropTypes.string,
  }),
};

export default memo(FGDoPendingActions);
